(() => {
  const dataElement = document.getElementById('SteepAovCartData');
  const drawer = document.getElementById('cart-drawer');
  if (!dataElement || !drawer) return;

  let config;
  try {
    config = JSON.parse(dataElement.textContent || '{}');
  } catch (error) {
    console.error('[Steep cart] Cart configuration is invalid.', error);
    return;
  }

  const rootUrl = window.Shopify?.routes?.root || '/';
  const packVariants = (config.packVariants || [])
    .map((variant) => ({ ...variant, packSize: Number.parseInt(variant.title, 10) || 0 }))
    .filter((variant) => [4, 12, 24].includes(variant.packSize));
  const exclusiveOfferVariantIds = new Set((config.exclusiveOfferVariantIds || []).map(String));
  const drinkProductIds = new Set(
    [config.weightedPurchaseProductId, config.drinkProductId, config.bundleParentProductId, ...(config.legacyDrinkProductIds || [])]
      .filter(Boolean)
      .map(String)
  );
  const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: config.currency || 'USD',
  });
  let cartRequest;
  let cartRevision = 0;
  let renderFrame;
  let mutationPromise = Promise.resolve();

  const money = (cents) => formatter.format((Number(cents) || 0) / 100);
  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
  const properties = (item) => item?.properties || {};
  const bundleId = (item) => String(properties(item)['_appstle-bb-id'] || '');
  const isChild = (item) => String(properties(item)['_appstle-bb-child'] || '').toLowerCase() === 'true';
  const itemPlan = (item) => item?.selling_plan_allocation?.selling_plan || null;
  const variantFor = (item) => packVariants.find((variant) => (String(variant.id) === String(item.variant_id) || String(variant.purchaseId) === String(item.variant_id)));
  const packSize = (item) => {
    const configured = variantFor(item)?.packSize;
    if (configured) return configured;
    if ((config.legacyDrinkProductIds || []).map(String).includes(String(item.product_id))) return 4;
    return Number.parseInt(item.variant_title || '', 10) || 0;
  };
  const isDrinkParent = (item) => !isChild(item) && drinkProductIds.has(String(item.product_id));
  const isExclusiveOffer = (item) => Boolean(
    properties(item)['_Trial offer']
    || properties(item)['_Landing page']
    || exclusiveOfferVariantIds.has(String(item.variant_id))
  );
  const isStandardBundleParent = (item) => !isChild(item)
    && Boolean(variantFor(item))
    && !isExclusiveOffer(item);
  const isMixedOfferCart = (cart) => cart.items.some(isExclusiveOffer)
    && cart.items.some(isStandardBundleParent);

  const getCart = () => {
    if (!cartRequest) {
      cartRequest = fetch(`${rootUrl}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      }).finally(() => { cartRequest = null; });
    }
    return cartRequest;
  };

  const postCart = async (path, body) => {
    const response = await fetch(`${rootUrl}${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || 'Cart update failed.');
    cartRevision += 1;
    cartRequest = null;
    return payload;
  };

  const refreshDrawer = async () => {
    const refreshUrl = new URL(window.location.href);
    refreshUrl.searchParams.set('section_id', 'cart-drawer-section');
    if (config.themeId && window.Shopify?.theme?.role === 'unpublished') refreshUrl.searchParams.set('preview_theme_id', String(config.themeId));
    else refreshUrl.searchParams.delete('preview_theme_id');
    const response = await fetch(refreshUrl.toString(), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error('Could not refresh the cart drawer.');
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = parsed.getElementById('shopify-section-cart-drawer-section');
    const current = document.getElementById('shopify-section-cart-drawer-section');
    if (!next || !current) throw new Error('Cart drawer section was unavailable.');
    const wasOpen = current.querySelector('#cart-drawer')?.hasAttribute('open');
    current.replaceChildren(...next.childNodes);
    if (wasOpen) document.getElementById('cart-drawer')?.open?.();
    scheduleRender();
  };

  // Separate new purchases from zero-weight legacy Appstle renewal parents.
  // Fail before changing the cart if catalog/plan staging is incomplete.
  const bundleLines = (variant, quantity = 1, sellingPlan = null) => {
    // Draft-theme staging keeps the current purchase path until all global gates pass.
    if (config.weightedPurchaseEnabled !== true) {
      return [{ id: Number(variant.id), quantity, ...(sellingPlan ? { selling_plan: Number(sellingPlan) } : {}) }];
    }
    const id = Number(variant.purchaseId);
    const configuredPlans = variant.sellingPlans || [];
    const purchasePlans = variant.purchaseSellingPlans || [];
    const sourcePlan = configuredPlans.find(plan => String(plan.id) === String(sellingPlan));
    const purchasePlan = purchasePlans.find(plan => String(plan.id) === String(sellingPlan));
    if (!Number.isSafeInteger(id) || id <= 0 || id === Number(variant.id)
      || !config.weightedPurchaseProductId || !variant.purchaseAvailable
      || Number(variant.purchasePrice) !== Number(variant.price)
      || (sellingPlan && (!sourcePlan || !purchasePlan
        || Number(sourcePlan.price) !== Number(purchasePlan.price)))) {
      throw new Error('This pack is temporarily unavailable. Please try again shortly.');
    }
    return [{ id, quantity, ...(sellingPlan ? { selling_plan: Number(sellingPlan) } : {}) }];
  };

  const groups = (cart) => {
    const result = new Map();
    cart.items.forEach((item) => {
      const id = bundleId(item);
      if (!id) return;
      if (!result.has(id)) result.set(id, { parents: [], children: [] });
      result.get(id)[isChild(item) ? 'children' : 'parents'].push(item);
    });
    return result;
  };

  const removeExclusiveOfferState = async (cart, force = false) => {
    const current = cart || await getCart();
    if (!force && !isMixedOfferCart(current)) return current;
    const exclusiveLines = current.items.filter(isExclusiveOffer);
    if (!exclusiveLines.length) return current;
    const updates = {};
    exclusiveLines.forEach((item) => { updates[item.key] = 0; });
    await postCart('cart/update.js', { updates });
    const verified = await getCart();
    if (verified.items.some(isExclusiveOffer)) throw new Error('The previous landing-page offer could not be removed safely.');
    return verified;
  };

  const normalizeBundles = async () => {
    const cart = await removeExclusiveOfferState(await getCart());
    const legacyParents = cart.items.filter((item) => isStandardBundleParent(item) && (Boolean(bundleId(item)) || (config.weightedPurchaseEnabled === true && String(item.variant_id) === String(variantFor(item).id))));
    const legacyChildren = cart.items.filter(isChild);
    if (!legacyParents.length && !legacyChildren.length) return cart;

    const updates = {};
    const additions = legacyParents.map((item) => bundleLines(
      variantFor(item),
      Number(item.quantity) || 1,
      itemPlan(item)?.id
    )[0]);
    legacyParents.forEach((item) => { updates[item.key] = 0; });
    legacyChildren.forEach((item) => { updates[item.key] = 0; });

    if (additions.length) await postCart('cart/add.js', { items: additions });
    await postCart('cart/update.js', { updates });
    const verified = await getCart();
    if (verified.items.some(isChild)
      || verified.items.some((item) => isStandardBundleParent(item) && (Boolean(bundleId(item)) || (config.weightedPurchaseEnabled === true && String(item.variant_id) === String(variantFor(item).id))))) {
      throw new Error('Legacy bundle lines could not be removed safely.');
    }
    return verified;
  };

  const addStandardBundle = async (items) => {
    setDrawerBusy(true);
    const operation = mutationPromise.then(async () => {
      const incoming = items.filter(item => String(item.properties?.['_appstle-bb-child'] || '').toLowerCase() !== 'true');
      const target = incoming[0];
      if (incoming.length !== 1 || !packVariants.some(v => String(v.id) === String(target.id))) {
        throw new Error('Please choose one Steep pack.');
      }
      const nativeLine = bundleLines(
        packVariants.find((variant) => String(variant.id) === String(target.id)),
        Number(target.quantity) || 1,
        target.selling_plan
      )[0];
      // Add first so an unavailable replacement leaves the customer's previous pack intact.
      await postCart('cart/add.js', { items: [nativeLine] });
      const afterAdd = await getCart();
      const keep = afterAdd.items.find(item => !isChild(item)
        && String(item.variant_id) === String(nativeLine.id)
        && String(itemPlan(item)?.id || '') === String(target.selling_plan || '')
        && !bundleId(item));
      if (!keep) throw new Error('The selected pack could not be verified.');
      const updates = { [keep.key]: Number(target.quantity) || 1 };
      afterAdd.items.forEach(item => {
        if (item.key === keep.key) return;
        if (isDrinkParent(item) || isExclusiveOffer(item) || isChild(item)) updates[item.key] = 0;
      });
      await postCart('cart/update.js', { updates });
      const verified = await normalizeBundles();
      if (verified.items.filter(isDrinkParent).length !== 1 || verified.items.some(isExclusiveOffer)) {
        throw new Error('The selected pack could not be confirmed. Please review your cart.');
      }
      // Keep old controls locked until the new line and section are both ready.
      await refreshDrawer();
      document.dispatchEvent(new CustomEvent('steep:cart-add-confirmed', { detail: { variantId: target.id } }));
      return verified;
    });
    mutationPromise = operation.catch(() => {}).finally(() => setDrawerBusy(false));
    return operation;
  };

  const matchPlan = (item, target) => {
    const source = itemPlan(item);
    if (!source) return null;
    const plans = target.sellingPlans || [];
    return plans.find((plan) => String(plan.id) === String(source.id))
      || plans.find((plan) => String(plan.name).toLowerCase() === String(source.name).toLowerCase())
      || null;
  };

  const setDrawerBusy = (busy) => {
    const currentDrawer = document.getElementById('cart-drawer');
    if (!currentDrawer) return;
    currentDrawer.classList.toggle('steep-cart-updating', busy);
    if (busy) {
      currentDrawer.setAttribute('aria-busy', 'true');
      currentDrawer.querySelectorAll('.cart-items__quantity-controls button, .cart-items__quantity-controls input, [data-steep-pack], [name="checkout"], #_si-sec-checkout-id, ._si-checkout-without-protection').forEach((control) => {
        if (!control.hasAttribute('data-steep-busy-disabled')) control.setAttribute('data-steep-busy-disabled', String(control.disabled));
        control.disabled = true;
      });
      if (!currentDrawer.querySelector('.steep-cart-update-status')) {
        const status = document.createElement('div');
        status.className = 'steep-cart-update-status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.textContent = 'Updating your cart…';
        currentDrawer.querySelector('.theme-drawer__dialog')?.append(status);
      }
      return;
    }
    currentDrawer.removeAttribute('aria-busy');
    currentDrawer.querySelectorAll('[data-steep-busy-disabled]').forEach((control) => {
      control.disabled = control.getAttribute('data-steep-busy-disabled') === 'true';
      control.removeAttribute('data-steep-busy-disabled');
    });
    currentDrawer.querySelector('.steep-cart-update-status')?.remove();
  };

  const replacePack = async (targetId, button) => {
    const target = packVariants.find((variant) => String(variant.id) === String(targetId));
    if (!target?.available) throw new Error('That pack is not currently available.');
    const cart = await normalizeBundles();
    const source = cart.items.find(isDrinkParent);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (!source) {
      await postCart('cart/add.js', { items: bundleLines(target) });
    } else {
      const plan = matchPlan(source, target);
      if (itemPlan(source) && !plan) throw new Error('The same subscription schedule is unavailable for that pack.');
      await postCart('cart/add.js', { items: bundleLines(target, Number(source.quantity) || 1, plan?.id) });
      const afterAdd = await getCart();
      const sourceId = bundleId(source);
      const removals = {};
      if (sourceId) {
        afterAdd.items.filter((item) => bundleId(item) === sourceId).forEach((item) => { removals[item.key] = 0; });
      } else {
        const sourcePlanId = itemPlan(source)?.id;
        afterAdd.items.filter((item) =>
          item.key === source.key
          || (String(item.variant_id) === String(source.variant_id)
            && String(itemPlan(item)?.id || '') === String(sourcePlanId || ''))
        ).forEach((item) => { removals[item.key] = 0; });
      }
      await postCart('cart/update.js', { updates: removals });
      const afterRemove = await getCart();
      const survivors = afterRemove.items.filter((item) => sourceId
        ? bundleId(item) === sourceId
        : String(item.variant_id) === String(source.variant_id));
      if (survivors.length) {
        const retry = {};
        survivors.forEach((item) => { retry[item.key] = 0; });
        await postCart('cart/update.js', { updates: retry });
      }
    }
    await normalizeBundles();
    await refreshDrawer();
  };

  const updateBundleQuantity = async (row, quantity) => {
    const cart = await getCart();
    const id = row.dataset.steepBundleId;
    const source = cart.items.find((item) => item.key === row.dataset.steepParentKey);
    const group = groups(cart).get(id);
    if (!source || !group?.children?.length) throw new Error('This pack could not be verified.');
    const updates = { [source.key]: Math.max(0, quantity) };
    const expected = group.parents.reduce((total, item) => {
      const count = item.key === source.key ? Math.max(0, quantity) : Number(item.quantity || 0);
      return total + packSize(item) * count;
    }, 0);
    updates[group.children[0].key] = expected;
    group.children.slice(1).forEach((item) => { updates[item.key] = 0; });
    await postCart('cart/update.js', { updates });
    await normalizeBundles();
    await refreshDrawer();
  };

  const card = (variant, current) => {
    const saving = current ? Math.max(0, Math.round((1 - (variant.price / variant.packSize) / (current.price / current.packSize)) * 100)) : 0;
    return `<button class="steep-aov-upgrade-card${variant.packSize === 24 ? ' steep-aov-upgrade-card--primary' : ''}" type="button" data-steep-pack="${escapeHtml(variant.id)}" data-target-pack-size="${variant.packSize}">
      <span class="steep-aov-upgrade-card__top"><span><strong class="steep-aov-upgrade-card__pack">${variant.packSize} Pack</strong><span class="steep-aov-upgrade-card__price">${escapeHtml(money(variant.price))}</span><span class="steep-aov-upgrade-card__value">${escapeHtml(money(variant.price / variant.packSize))} / can${saving ? ` · ${saving}% less per can` : ''}</span></span>${variant.image ? `<img class="steep-aov-upgrade-card__image" src="${escapeHtml(variant.image)}" alt="${variant.packSize} pack of Steep">` : ''}</span>
      <span class="steep-aov-upgrade-card__action">${current ? `Upgrade to ${variant.packSize}` : `Add the ${variant.packSize} pack`}</span>
    </button>`;
  };

  const renderShipping = (cart, currentDrawer) => {
    const summary = currentDrawer.querySelector('.cart-drawer__summary');
    if (!summary || !cart.item_count) return;
    let shipping = summary.querySelector('.steep-aov-shipping');
    if (!shipping) {
      shipping = document.createElement('div');
      shipping.className = 'steep-aov-shipping';
      summary.prepend(shipping);
    }
    const threshold = Number(config.shippingThresholdCents || 0);
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = threshold ? Math.min(100, cart.total_price / threshold * 100) : 100;
    shipping.style.setProperty('--steep-shipping-progress', `${progress}%`);
    shipping.innerHTML = `<div class="steep-aov-shipping__status"><span>${remaining ? `${escapeHtml(money(remaining))} to free shipping` : 'Free shipping unlocked'}</span><small>${escapeHtml(config.shippingEligibilityLabel || '')}</small></div><div class="steep-aov-shipping__track" role="progressbar" aria-label="Free shipping progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(progress)}"><div class="steep-aov-shipping__fill"></div></div>`;
  };

  const render = async () => {
    const currentDrawer = document.getElementById('cart-drawer');
    if (!currentDrawer) return;
    const revision = cartRevision;
    const cart = await getCart().catch(() => null);
    if (!cart) return;
    if (revision !== cartRevision) {
      scheduleRender();
      return;
    }
    const grouped = groups(cart);
    const rows = new Map([...currentDrawer.querySelectorAll('.cart-items__table-row')].map((row) => [row.dataset.key, row]));
    const currentKeys = new Set(cart.items.map((item) => item.key));
    rows.forEach((row, key) => {
      if (key && !currentKeys.has(key)) {
        row.remove();
        rows.delete(key);
      }
    });
    currentDrawer.querySelectorAll('[data-cart-subtotal]').forEach((node) => {
      const total = money(cart.total_price);
      node.textContent = total;
      node.setAttribute('value', total);
    });
    currentDrawer.querySelectorAll('.cart-items__table > caption span').forEach((node) => {
      node.textContent = money(cart.total_price);
    });
    grouped.forEach(({ parents, children }, id) => {
      children.forEach((item) => {
        const row = rows.get(item.key);
        if (row) { row.hidden = true; row.setAttribute('aria-hidden', 'true'); }
      });
      parents.forEach((item) => {
        const row = rows.get(item.key);
        const variant = variantFor(item);
        if (!row || !variant) return;
        row.dataset.steepBundleId = id;
        row.dataset.steepParentKey = item.key;
        row.dataset.steepVariantId = String(item.variant_id);
        row.dataset.steepParentQuantity = String(item.quantity);
        row.classList.add('steep-aov-bundle-parent');
        const controls = row.querySelector('.cart-items__quantity-controls');
        if (controls && !controls.querySelector('[data-steep-bundle-action]')) controls.innerHTML = `<div class="steep-aov-linked-quantity" aria-label="Quantity for ${variant.packSize} pack"><button type="button" data-steep-bundle-action="decrease" aria-label="Decrease ${variant.packSize} pack quantity">−</button><span aria-live="polite">${item.quantity}</span><button type="button" data-steep-bundle-action="increase" aria-label="Increase ${variant.packSize} pack quantity">+</button></div><button type="button" class="steep-aov-linked-remove" data-steep-bundle-action="remove" aria-label="Remove ${variant.packSize} pack">Remove</button>`;
      });
    });
    const linkedChildKeys = new Set(
      [...grouped.values()]
        .filter(({ parents }) => parents.some((item) => variantFor(item)))
        .flatMap(({ children }) => children.map((item) => item.key))
    );
    const logicalCount = cart.items
      .filter((item) => !linkedChildKeys.has(item.key))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);
    document.querySelectorAll('.cart-bubble__text-count').forEach((node) => { node.textContent = String(logicalCount); });
    currentDrawer.querySelectorAll('.theme-drawer__badge, [ref="cartItemCount"]').forEach((node) => { node.textContent = String(logicalCount); });
    [...currentDrawer.querySelectorAll('*')].filter((node) => node.children.length === 0 && /^total items in cart:\s*\d+$/i.test(node.textContent?.trim() || '')).forEach((node) => { node.textContent = `Total items in cart: ${logicalCount}`; });
    renderShipping(cart, currentDrawer);
    const content = currentDrawer.querySelector('.cart-drawer__content');
    const items = currentDrawer.querySelector('.cart-drawer__items');
    if (!content || !items || !cart.item_count) return;
    let extras = content.querySelector('.steep-aov-extras');
    if (!extras) {
      extras = document.createElement('div');
      extras.className = 'steep-aov-extras';
      items.append(extras);
    }
    const source = cart.items.find(isDrinkParent);
    const configuredCurrent = source ? variantFor(source) : null;
    const currentVariant = configuredCurrent || (source ? { packSize: packSize(source) || 4, price: Number(source.final_price || source.price || 0) } : null);
    const choices = packVariants.filter((variant) => variant.available && (!currentVariant || variant.packSize > currentVariant.packSize));
    const markup = choices.length ? `<section class="steep-aov-section steep-aov-offers" aria-label="Larger packs"><div class="steep-aov-section__heading"><h3>The smarter next pack.</h3><small>Optional</small></div><div class="steep-aov-upgrade-grid${choices.length === 1 ? ' steep-aov-upgrade-grid--single' : ''}">${choices.map((variant) => card(variant, currentVariant)).join('')}</div><p class="steep-aov-feedback" role="status" aria-live="polite"></p></section>` : '';
    if (extras.dataset.signature !== markup) {
      extras.innerHTML = markup;
      extras.dataset.signature = markup;
    }
  };

  const scheduleRender = () => {
    if (renderFrame) return;
    renderFrame = requestAnimationFrame(() => { renderFrame = 0; render(); });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-steep-pack]');
    if (!button || button.disabled) return;
    event.preventDefault();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setDrawerBusy(true);
    mutationPromise = mutationPromise.then(() => replacePack(button.dataset.steepPack, button)).catch((error) => {
      const feedback = document.querySelector('.steep-aov-feedback');
      if (feedback) feedback.textContent = error.message || 'Could not update the cart.';
      setDrawerBusy(false);
      const currentButton = document.querySelector(`[data-steep-pack="${CSS.escape(button.dataset.steepPack || '')}"]`);
      if (currentButton) {
        currentButton.disabled = false;
        currentButton.removeAttribute('aria-busy');
      }
    });
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-steep-bundle-action]');
    const row = button?.closest('.steep-aov-bundle-parent');
    if (!button || !row) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const current = Number(row.dataset.steepParentQuantity || 1);
    const action = button.dataset.steepBundleAction;
    const quantity = action === 'remove' ? 0 : action === 'decrease' ? Math.max(0, current - 1) : current + 1;
    mutationPromise = mutationPromise.then(() => updateBundleQuantity(row, quantity)).catch((error) => console.error('[Steep cart] Bundle update failed.', error));
  }, true);

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('#cart-drawer [name="checkout"], #cart-drawer a[href^="/checkout"]');
    if (!checkout || checkout.dataset.steepVerified === 'true') return;
    const isShipInsureCheckout = checkout.id === '_si-sec-checkout-id'
      || [...checkout.classList].some((className) => className.startsWith('_si-checkout-'));
    event.preventDefault();
    event.stopImmediatePropagation();
    checkout.disabled = true;
    mutationPromise = mutationPromise.then(normalizeBundles).then(async () => {
      checkout.dataset.steepVerified = 'true';
      checkout.disabled = false;
      await document.getElementById('cart-drawer')?.close?.();
      if (isShipInsureCheckout) checkout.click();
      else window.location.assign('/checkout');
    }).catch((error) => {
      checkout.disabled = false;
      console.error('[Steep cart] Checkout blocked because pack integrity could not be verified.', error);
    });
  }, true);

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    const currentDrawer = document.getElementById('cart-drawer');
    if (!link || !currentDrawer?.hasAttribute('open') || link.closest('#cart-drawer')) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;
    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin || destination.href === window.location.href) return;
    currentDrawer.close?.();
  }, true);

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.target.closest?.('#shopify-section-cart-drawer-section'))) scheduleRender();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', scheduleRender);
  window.addEventListener('pageshow', () => {
    cartRequest = null;
    // A restored page can have stale/empty rows even though checkout kept the cart.
    // Rendering totals alone cannot create missing rows; rebuild the section first.
    setDrawerBusy(true);
    mutationPromise = mutationPromise.then(refreshDrawer).catch((error) => {
      console.error('[Steep cart] Could not refresh the restored cart.', error);
    }).finally(() => {
      setDrawerBusy(false);
      scheduleRender();
    });
  });
  window.SteepCart = {
    open: () => document.getElementById('cart-drawer')?.open?.(),
    refresh: refreshDrawer,
    normalize: normalizeBundles,
    addStandardBundle,
  };
  scheduleRender();
  if (new URLSearchParams(window.location.search).get('open_cart') === '1') {
    customElements.whenDefined('theme-drawer').then(() => document.getElementById('cart-drawer')?.open?.());
  }
})();
